import { Target, TrendingUp, Zap, DollarSign, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { BUDGET_TIERS } from '../../utils/budgetTiers';

interface BudgetTierCardsProps {
  floorPrice: number;
  selectedTier: number | null;
  customBudget: string;
  onTierSelect: (tierId: number, budgetValue: number) => void;
  onCustomBudgetChange: (value: string) => void;
  error?: string;
}

const TIER_ICONS = {
  target: Target,
  'trending-up': TrendingUp,
  zap: Zap,
};

export default function BudgetTierCards({
  floorPrice,
  selectedTier,
  customBudget,
  onTierSelect,
  onCustomBudgetChange,
  error,
}: BudgetTierCardsProps) {
  const customBudgetNum = parseFloat(customBudget) || 0;
  const isEmpty = customBudget === '' || customBudget === '0';
  const isCustomActive = selectedTier === null && customBudgetNum > 0;
  const isBelowFloor = !isEmpty && customBudgetNum < floorPrice;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="p-5 rounded-xl bg-white/[0.02] border border-gray-700/30">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-neutral-light">Target Daily Volume</h3>
        </div>
        <p className="text-xs text-text-muted">
          Select a tier to auto-set your daily budget.{' '}
          <span className="text-accent underline">
            Actual bids per location will be set in the next step.
          </span>
        </p>

        {/* Tier Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {BUDGET_TIERS.map((tier) => {
            const budget = tier.calculateBudget(floorPrice);
            const isSelected = selectedTier === tier.id;
            const Icon = TIER_ICONS[tier.icon];

            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => onTierSelect(tier.id, budget)}
                className={`relative rounded-xl p-4 border-2 text-left transition-all duration-200 ${
                  isSelected
                    ? 'border-accent bg-accent/5'
                    : 'border-gray-700/50 bg-white/5 hover:border-gray-600 hover:bg-white/[0.07]'
                }`}
              >
                {/* Selected Badge */}
                {isSelected && (
                  <div className="absolute top-2.5 right-2.5">
                    <CheckCircle2 className="w-5 h-5 text-accent fill-accent/20" />
                  </div>
                )}

                {/* Icon */}
                <div className="flex justify-center mb-3">
                  <div
                    className={`p-2 rounded-lg ${
                      isSelected ? 'bg-accent/10' : 'bg-white/5'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isSelected ? 'text-accent' : 'text-text-muted'
                      }`}
                    />
                  </div>
                </div>

                {/* Tier Name */}
                <p
                  className={`text-sm font-semibold text-center ${
                    isSelected ? 'text-neutral-light' : 'text-neutral-light'
                  }`}
                >
                  {tier.name}
                </p>

                {/* Leads/Day */}
                <p className="text-xs text-text-muted text-center mt-0.5">
                  {tier.leadsPerDay} Lead{tier.leadsPerDay !== '~1' ? 's' : ''} / Day
                </p>

                {/* Price */}
                <p
                  className={`text-lg font-bold text-center mt-2 ${
                    isSelected ? 'text-accent' : 'text-neutral-light'
                  }`}
                >
                  ${budget}
                  <span className="text-xs font-normal text-text-muted">/day</span>
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700/30" />

      {/* Custom Daily Budget */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Custom Daily Budget
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <DollarSign className="h-5 w-5 text-text-muted" />
          </div>
          <input
            type="number"
            value={customBudget}
            onChange={(e) => onCustomBudgetChange(e.target.value)}
            className={`w-full max-w-xs pl-10 pr-4 py-3 border rounded-lg text-neutral-light placeholder-text-muted focus:outline-none transition-colors ${
              isBelowFloor
                ? 'bg-rose-500/10 border-rose-400/60 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/20'
                : isCustomActive
                  ? 'bg-white/5 border-accent focus:border-accent focus:ring-2 focus:ring-accent/20'
                  : 'bg-white/5 border-gray-700/50 focus:border-accent/50 focus:ring-2 focus:ring-accent/20'
            }`}
            placeholder="0.00"
            min={floorPrice}
            step="1"
          />
        </div>
        {isBelowFloor && (
          <p className="flex items-center gap-1.5 text-xs text-rose-400">
            <AlertCircle className="w-3.5 h-3.5" />
            Minimum budget for this claim type is ${floorPrice}.
          </p>
        )}
        {!isBelowFloor && isCustomActive && (
          <p className="flex items-center gap-1.5 text-xs text-accent">
            <Info className="w-3.5 h-3.5" />
            Custom budget active. Volume estimates updated in sidebar.
          </p>
        )}
        {error && !isBelowFloor && (
          <div className="flex items-center gap-2 mt-1.5">
            <AlertCircle className="w-4 h-4 text-rose-400" />
            <span className="text-xs text-rose-400">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}
